import { ChevronRight } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { useSnackbar } from "notistack";
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../Apis/Apis';
import { BudgetPricingComponentModel, BudgetPricingStrategyModel, CommodityModel, ContractDate, CounterpartyModel, GetCustomerDataResponse, GetCustomerSettingsResponse, LocationModel, UpdateBudgetModel } from '../../../../../Generated/Commodity-Management-Api';
import { findMatchingPricingStrategies } from '../../../../../Helpers';
import { useLoadingState } from '../../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { StoneXLoading, StoneXRow } from '../../../../StoneX';
import { StoneXFileUploader } from '../../../../StoneXMui';

interface UploadBudgetsProps {
    //onUploadBudgetsUpdated: (budgets: BudgetModel[] | undefined) => void;
    pricingStrategies?: BudgetPricingStrategyModel[];
}

export default function UploadBudgets(props: UploadBudgetsProps) {

    const { pricingStrategies } = props;

    const { enqueueSnackbar } = useSnackbar();
    const savingState = useLoadingState();

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

    const [expanded, setExpanded] = useState(false);
    const [invalidItems, setInvalidItems] = useState<{ type: string; name: string; row: number }[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    function getUploadHeaders(includeRequiredMarker: boolean): string[] {
        const suffix = includeRequiredMarker ? '*' : '';

        return [
            `Commodity${suffix}`,
            ...(customerSettings.budgetsByCounterparty ? [`Counterparty${suffix}`] : []),
            ...(customerSettings.budgetsByLocation ? [`Location${suffix}`] : []),
            ...(customerSettings.usesBudgetRounds ? [`Budget Round${suffix}`] : []),
            `Budget Month${suffix}`,
            `Budget Year${suffix}`,
            `Contract Month${suffix}`,
            `Contract Year${suffix}`,
            `Volume${suffix}`,
            `Unit${suffix}`,
            `Currency${suffix}`,
            `Price${suffix}`,
        ];
    }

    // A cell may hold either the id from the template's lookup sheets or the name
    // shown next to it. Names are matched exactly, ignoring case and surrounding
    // whitespace, against the active site's customer data only.
    function findEntity<T extends Record<string, any>>(items: T[] | null | undefined, idField: string, nameField: string, value: unknown): T | undefined {
        if (value === undefined || value === null || value === '') {
            return undefined;
        }

        const asNumber = typeof value === 'number' ? value : Number(String(value).trim());
        if (!Number.isNaN(asNumber)) {
            const byId = items?.find((item) => item[idField] === asNumber);
            if (byId) {
                return byId;
            }
        }

        const asName = String(value).trim().toLowerCase();
        return items?.find((item) => String(item[nameField] ?? '').trim().toLowerCase() === asName);
    }

    function getRowEntities(row: any) {
        return {
            commodity: findEntity(customerData.commodities, 'commodityId', 'commodityName', row['Commodity']),
            location: findEntity(customerData.locations, 'locationId', 'locationName', row['Location']),
            counterparty: findEntity(customerData.counterparties, 'counterpartyId', 'counterpartyName', row['Counterparty']),
            budgetRound: findEntity(customerData.budgetRounds, 'budgetRoundId', 'budgetRoundName', row['Budget Round']),
        };
    }

    // Helper to map a row to UpdateBudgetModel
    function mapRowToUpdateBudgetModel(row: any): UpdateBudgetModel {

        const { commodity, location, counterparty, budgetRound } = getRowEntities(row);
        const pricingStrategy = identifyPricingStrategy(commodity, location, counterparty);

        return {
            budgetId: 0,
            commodityId: commodity?.commodityId,
            locationId: location?.locationId,
            counterpartyId: counterparty?.counterpartyId,
            budgetRoundId: budgetRound?.budgetRoundId,
            locationName: location?.locationName ?? row.Location,
            budgetRound: budgetRound?.budgetRoundName ?? row['Budget Round'],
            budgetDate: { year: row['Budget Year'], month: row['Budget Month'] } as ContractDate,
            contractDate: { year: row['Contract Year'], month: row['Contract Month'] } as ContractDate,
            volume: row.Volume,
            unit: row.Unit,
            currency: row.Currency,
            price: row.Price,
            pricingStrategyId: pricingStrategy?.budgetPricingStrategyId,
            // Optional chaining, not `!`: a row with no matching strategy used to throw
            // here and take the whole upload down. Those rows are reported as invalid
            // before this runs, so this is belt and braces.
            budgetPrices: (pricingStrategy?.components ?? []).map((component: BudgetPricingComponentModel) => ({
                budgetPriceId: 0,
                componentType: component.componentType,
                unit: row.Unit,
                price: row.Price,
            }))
        } as UpdateBudgetModel;
    }

    function getBudgetsFromUpload(file: File) {

        const requiredHeaders = getUploadHeaders(false);

        const reader = new FileReader();
        reader.onload = (event) => {
            const data = new Uint8Array(event.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Get headers from the first row
            const headerRow: string[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[] || [];

            // Remove '*' from requiredHeaders before comparing
            const normalizedRequiredHeaders = requiredHeaders.map(h => h.replace(/\*/g, ''));
            const normalizedHeaderRow = headerRow.map(h => h.replace(/\*/g, ''));
            const missingHeaders = normalizedRequiredHeaders.filter(h => !normalizedHeaderRow.includes(h));
            if (missingHeaders.length > 0) {
                setInvalidItems([
                    { type: "Header", name: `Missing: ${missingHeaders.join(", ")}`, row: 1 }
                ]);
                console.warn("Missing required headers:", missingHeaders);
                return;
            }

            // Remove '*' from any header columns
            let rows: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 0 });
            if (rows.length > 0) {
                const firstRow = rows[0];
                const newKeys = Object.keys(firstRow).map(key => key.replace(/\*/g, ''));
                rows = rows.map(row => {
                    const newRow: any = {};
                    Object.keys(row).forEach((key, idx) => {
                        newRow[newKeys[idx]] = row[key];
                    });
                    return newRow;
                });
            }

            // Find invalid commodities and their row numbers
            const invalids: { type: string; name: string; row: number }[] = [];
            const updateBudgetModels: UpdateBudgetModel[] = [];

            rows.forEach((row, idx) => {

                let anyInvalid = false;
                const entities = getRowEntities(row);

                const validateColumn = (column: string, entity: unknown) => {
                    if (!entity) {
                        invalids.push({ type: column, name: row[column], row: idx + 2 });
                        anyInvalid = true;
                    }
                };

                validateColumn('Commodity', entities.commodity);
                if (customerSettings.budgetsByCounterparty)
                    validateColumn('Counterparty', entities.counterparty);
                if (customerSettings.budgetsByLocation)
                    validateColumn('Location', entities.location);
                if (customerSettings.usesBudgetRounds)
                    validateColumn('Budget Round', entities.budgetRound);

                // Same check the manual entry screen makes: no strategy covering the row
                // means no price components to write, so say which row and why instead of
                // importing a budget with no price.
                if (!anyInvalid) {
                    const { commodity, location, counterparty } = entities;
                    if (!identifyPricingStrategy(commodity, location, counterparty)) {
                        invalids.push({
                            type: 'Pricing Strategy',
                            name: 'No single pricing strategy covers this row',
                            row: idx + 2,
                        });
                        anyInvalid = true;
                    }
                }

                // Add to UpdateBudgetModel array if valid.
                if (!anyInvalid) {
                    updateBudgetModels.push(mapRowToUpdateBudgetModel(row));
                }
            });

            setInvalidItems(invalids);

            if (invalids.length > 0) {
                console.warn("Invalid commodities found in upload:", invalids);
                return;
            }

            // Step 4: If all commodities are valid, proceed with API call
            saveBudgets(updateBudgetModels);
        };
        reader.readAsArrayBuffer(file);
    }

    // Shares the matcher with the manual entry screen so an upload and a hand-entered
    // budget can never disagree about which strategy applies. Still requires exactly one
    // match: there is nobody to ask which one during a bulk import.
    function identifyPricingStrategy(selectedCommodity: CommodityModel | undefined, selectedLocation: LocationModel | undefined, selectedCounterparty: CounterpartyModel | undefined) {
        const matches = findMatchingPricingStrategies(pricingStrategies, {
            commodityId: selectedCommodity?.commodityId,
            locationId: selectedLocation?.locationId ?? null,
            counterpartyId: selectedCounterparty?.counterpartyId ?? null,
        });

        return matches.length === 1 ? matches[0] : null;
    }

    async function saveBudgets(b: UpdateBudgetModel[]) {
        setIsSaving(true);
        const budgets = { budgets: b };

        CommodityManagementApi.updateBudgets(savingState.setLoadingState, budgets)
            .then(async (res) => {
                enqueueSnackbar('Budgets were successfully added', { variant: 'success' });
                setIsSaving(false);
            })
            .catch(err => {
                enqueueSnackbar('An error occurred while saving the budgets', { variant: 'error' });
                setIsSaving(false);
            });
    }

    function downloadTemplate() {
        const requiredHeaders = getUploadHeaders(true);

        // Main template sheet (headers only)
        const templateSheet = XLSX.utils.aoa_to_sheet([requiredHeaders]);

        // Set column widths to fit header text
        templateSheet['!cols'] = requiredHeaders.map(header => ({
            wch: Math.max(header.length + 2, 12)
        }));

        // Commodities tab
        const commoditiesSheet = XLSX.utils.json_to_sheet(
            (customerData.commodities ?? []).map(c => ({
                CommodityId: c.commodityId,
                CommodityName: c.commodityName
            }))
        );

        // Locations tab (if enabled)
        let locationsSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.budgetsByLocation) {
            locationsSheet = XLSX.utils.json_to_sheet(
                (customerData.locations ?? []).map(l => ({
                    LocationId: l.locationId,
                    LocationName: l.locationName
                }))
            );
        }

        // Counterparties tab (if enabled)
        let counterpartiesSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.budgetsByCounterparty) {
            counterpartiesSheet = XLSX.utils.json_to_sheet(
                (customerData.counterparties ?? []).map(c => ({
                    CounterpartyId: c.counterpartyId,
                    CounterpartyName: c.counterpartyName
                }))
            );
        }

        // Budget Rounds tab (if enabled)
        let budgetRoundsSheet: XLSX.WorkSheet | undefined;
        if (customerSettings.usesBudgetRounds) {
            budgetRoundsSheet = XLSX.utils.json_to_sheet(
                (customerData.budgetRounds ?? []).map(b => ({
                    BudgetRoundId: b.budgetRoundId,
                    BudgetRoundName: b.budgetRoundName,
                }))
            );
        }

        // Build workbook
        const sheets: { [name: string]: XLSX.WorkSheet } = {
            "Budgets Template": templateSheet,
            "Commodities": commoditiesSheet
        };
        if (locationsSheet) sheets["Locations"] = locationsSheet;
        if (counterpartiesSheet) sheets["Counterparties"] = counterpartiesSheet;
        if (budgetRoundsSheet) sheets["Budget Rounds"] = budgetRoundsSheet;

        const workbook: XLSX.WorkBook = {
            SheetNames: Object.keys(sheets),
            Sheets: sheets
        };

        // Write and trigger download
        const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "BudgetsTemplate.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return (
        <div style={{ borderTop: '1px solid var(--gray5)', borderBottom: '1px solid var(--gray5)' }}>
            <StoneXRow align="center" marginDirection="topAndBottom">
                <IconButton onClick={() => setExpanded((previous) => !previous)}>
                    <ChevronRight sx={{ transform: expanded ? 'rotate(90deg)' : undefined }} />
                </IconButton>
                <h3 style={{ color: 'var(--gray2)' }}>Upload Budgets</h3>
            </StoneXRow>

            {expanded && <>
                <div style={{ fontSize: 'small' }}>
                    <Button variant='text' onClick={downloadTemplate}>Download budget template</Button>
                    (* indicates required field.)
                </div>
                <StoneXRow align='start'>
                    <StoneXFileUploader message="Upload budgets" onUpload={getBudgetsFromUpload}></StoneXFileUploader>
                    {/* Table for invalid commodities with row numbers */}
                    {invalidItems.length > 0 && (
                        <div style={{ margin: '16px 0' }}>
                            <h4 style={{ color: 'red' }}>Invalid Entries in Upload</h4>
                            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Type</th>
                                        <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Name</th>
                                        <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Row Number</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invalidItems.map(({ type, name, row }, idx) => (
                                        <tr key={name + '_' + row + '_' + idx}>
                                            <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{type}</td>
                                            <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{name}</td>
                                            <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{row}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <StoneXLoading show={savingState.isLoading()} />
                </StoneXRow>
            </>}
        </div>
    );
}
